import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import clsx from "clsx";
import { UNIFIED_MODEL_TABS } from "@/lib/constants";
import { Loader2, Database, Code, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import ArchitectureTabLayout from "./architecture-tab-layout";
import FunctionalTabLayout from "./functional-tab-layout";
import NodeHistoryTabLayout from "./node-history-tab-layout";
import { ArchitectureComponent } from "@/types/unified-model";
import { flattenArchitecturalSpecs } from "@/lib/utils";
import DesignTabLayout from "./design-tab-layout";
import { useGetGraph } from "@/service-hooks/use-neo4j-graph-service";
import useGenerateArchitectureService from "@/service-hooks/use-generate-architecture-service";
import { useGetFunctionalGraphService } from "@/service-hooks/use-get-functional-graph-service";
import { useGetCodeOntologies, CodeOntology } from "@/service-hooks/use-code-ontology-service";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { usePanelProps } from "@/context/panel-props-context";
import { usePanel } from "@/context/panel-context";
import { apiFetch } from "@/lib/api-client.ts";
import ConfirmDialog from "@/components/elements/confirm-dialog";
import { config } from "@/config";
import { useProjectRole } from "@/context/project-role-context";
import CustomTooltip from "@/components/elements/custom-tooltip";

// Header Actions Component for global actions
function UnifiedModelHeaderActions({
  isLoading,
  onOpenNeo4jPopup,
}: {
  isLoading: boolean;
  onOpenNeo4jPopup: () => void;
}) {
  return (
    <Button
      onClick={onOpenNeo4jPopup}
      variant="outline"
      size="sm"
      disabled={isLoading}
      className="flex items-center gap-2 bg-surface-card border-surface-border text-white hover:bg-surface-hover hover:border-primary disabled:opacity-50"
    >
      <Database className="w-4 h-4" />
      Visualize in Graph
    </Button>
  );
}

export default function UnifiedModelLayout({
  onHeaderActionsReady,
}: {
  onHeaderActionsReady?: (actions: React.ReactNode) => void;
} = {}) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { panelProps } = usePanelProps();
  const { setPanelType } = usePanel();
  const { isViewer } = useProjectRole();

  const [tabs, setTabs] = useState(UNIFIED_MODEL_TABS);
  const [activeTab, setActiveTab] = useState<string>("");

  // State for Neo4j popup

  // State for architecture generation confirmation
  const [isArchitectureConfirmOpen, setArchitectureConfirmOpen] = useState(false);

  const [architectureData, setArchitectureData] = useState<
    ArchitectureComponent[] | any
  >([]);
  // Track availability for dynamic tab control
  const [hasArchitecture, setHasArchitecture] = useState(false);

  // NEW: State to control when to load architecture data
  const [shouldLoadArchitecture, setShouldLoadArchitecture] = useState(false);

  // State to control when to load code ontologies
  const [shouldLoadCode, setShouldLoadCode] = useState(false);

  // State to force remount of FunctionalTabLayout when data changes
  const [functionalRefreshKey, setFunctionalRefreshKey] = useState(0);

  // Check if functional data exists (personas are the root of functional model)
  const { data: functionalData } = useGetFunctionalGraphService({
    projectUuid: id,
    label: "Persona",
    limit: 10, // Only need to check if at least one exists
    page: 1,
    enabled: !!id,
  });

  // Determine if functional data exists
  const hasFunctionalData = (functionalData?.total ?? 0) > 0;

  // NEW: Architecture data loading hook - only enabled when Architecture tab is active
  const {
    data: architectureGraphData,
    isLoading: isLoadingArchitecture,
    refetch: refetchArchitecture,
  } = useGetGraph(id, shouldLoadArchitecture && activeTab === "Architecture");

  // Code ontologies loading hook - only enabled when Code tab is active
  const {
    data: codeOntologiesData,
    isLoading: isLoadingCodeOntologies,
    refetch: refetchCodeOntologies,
  } = useGetCodeOntologies(shouldLoadCode ? id : "", 1, 50);

  // Generate Architecture mutation
  const { mutate: generateArchitecture } = useGenerateArchitectureService(
    (data, error) => {
      if (!error && data) {
        // Refetch architecture data after successful generation
        refetchArchitecture();
      }
    }
  );

  // "Visualize in Graph": close the Semantic Model right-side panel and
  // navigate to the full-page knowledge graph. Closing the panel first
  // prevents it from lingering as an overlay on top of the graph page —
  // previously only the navigation ran, so users landed on
  // `/knowledge-graph/:id` with the Semantic Model still open over it.
  const handleOpenNeo4jPopup = useCallback(() => {
    if (!id) return;
    setPanelType(null);
    navigate(`/knowledge-graph/${id}`);
  }, [id, navigate, setPanelType]);

  const handleConfirmGenerateArchitecture = useCallback(() => {
    if (!id) return;
    generateArchitecture({
      uuid: id,
      agent: "architecture_agent",
    });
    setArchitectureConfirmOpen(false);
  }, [id, generateArchitecture]);

  const handleImportCsv = useCallback(async () => {
    if (isViewer) {
      toast.error("You have view-only access to this project");
      return;
    }
    if (!id) {
      toast.error("Project ID not found.");
      return;
    }

    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv";
    input.onchange = async (e: any) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // ✅ Strict validation for CSV files only
      const fileName = file.name.toLowerCase();
      const fileType = file.type;

      const validMimeTypes = [
        "text/csv",
        "application/csv",
        "application/vnd.ms-excel", // sometimes used for CSV
      ];

      const isCsvFile =
        fileName.endsWith(".csv") &&
        validMimeTypes.some(
          (mime) => fileType.includes(mime) || fileType === ""
        );

      if (!isCsvFile) {
        toast.error(
          "Invalid file format. Please upload a valid .csv file only."
        );
        return;
      }

      // Validate CSV headers before sending to n8n
      const REQUIRED_HEADERS = ["persona", "outcome", "scenario", "step", "action", "api"];
      try {
        const text = await file.text();
        const firstLine = text.split(/\r?\n/)[0];
        if (!firstLine) {
          toast.error("CSV file is empty.");
          return;
        }
        const headers = firstLine.split(",").map((h: string) => h.trim().toLowerCase());
        const missingHeaders = REQUIRED_HEADERS.filter((h) => !headers.includes(h));
        if (missingHeaders.length > 0) {
          toast.error(
            `Invalid CSV headers. Missing: ${missingHeaders.join(", ")}. Required headers: ${REQUIRED_HEADERS.join(", ")}`
          );
          return;
        }
      } catch {
        toast.error("Unable to read CSV file.");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("data", file);
        formData.append("uuid", id);

        toast.loading("Uploading and processing CSV...");

        const res = await apiFetch(
          `${config.n8nUrl}/webhook/csv-to-functional-graph`,
          {
            method: "POST",
            body: formData,
          }
        );

        const result = await res.json();
        toast.dismiss();

        // 🚨 Handle error response
        if (result?.error && Array.isArray(result.error)) {
          toast.error(
            "CSV file has issues. Please review the error details below."
          );

          // Build HTML for modal
          const errorList = result.error
            .map(
              (err: any) => `
              <div class="p-2 border-b border-surface-border text-sm">
                <strong>Row ${err.row}:</strong> ${err.issue}
                <br /><span class="text-muted">${err.suggestion}</span>
              </div>`
            )
            .join("");

          const modal = document.createElement("div");
          modal.className =
            "fixed inset-0 bg-black/70 flex items-center justify-center z-[9999]";
          modal.innerHTML = `
          <div class="bg-[#1e1e1e] text-white rounded-lg shadow-xl w-[90%] max-w-2xl p-6">
            <h2 class="text-lg font-semibold mb-3">CSV Import Errors</h2>
            <div class="max-h-96 overflow-y-auto border border-surface-border rounded-md mb-4 bg-surface">
              ${errorList}
            </div>
            <div class="text-right">
              <button id="close-error-modal" class="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary/80">
                Close
              </button>
            </div>
          </div>`;
          document.body.appendChild(modal);
          document
            .getElementById("close-error-modal")
            ?.addEventListener("click", () => modal.remove());

          return;
        }

        // ✅ Success case
        toast.success(
          result?.Message || result?.message || "CSV imported successfully!"
        );
      } catch (err) {
        console.error(err);
        toast.dismiss();
        toast.error("Error during import. Please check your file and retry.");
      }
    };

    input.click();
  }, [id, isViewer]);

  // Visualize Architecture handler - navigates to architecture agent and generates diagram
  const handleVisualizeArchitecture = useCallback(() => {
    const targetPath = `/chat/architecture_agent/${id}`;
    const isAlreadyOnChatPage = location.pathname === targetPath;

    // Close the side panel before navigating
    setPanelType(null);

    if (isAlreadyOnChatPage) {
      // If already on chat page, trigger blueprint generation immediately
      window.dispatchEvent(
        new CustomEvent("generateBlueprintFromUnifiedModel")
      );
    } else {
      // The page generates the blueprint itself once it has mounted (architecture-agent-layout.tsx)
      navigate(targetPath, { state: { generateBlueprint: true } });
    }
  }, [id, navigate, location.pathname, setPanelType]);

  // Export functionality moved to tab-specific sections if needed

  // NEW: Handle tab change and load architecture data when Architecture tab is selected
  const handleTabChange = useCallback(
    (tabValue: string) => {
      setActiveTab(tabValue);

      // Load architecture data when Architecture tab is clicked
      if (tabValue === "Architecture") {
        setShouldLoadArchitecture(true);

        // If we already have the query enabled, refetch to ensure fresh data
        if (shouldLoadArchitecture) {
          refetchArchitecture();
        }
      }

      // Refetch functional data when Functional tab is clicked
      if (tabValue === "Functional") {
        // Invalidate all functional graph queries and force immediate refetch
        queryClient.invalidateQueries({
          queryKey: ["functional-graph"],
          refetchType: "active",
        });
        // Also reset query data to ensure fresh fetch
        queryClient.resetQueries({
          queryKey: ["functional-graph"],
          exact: false,
        });
        // Force remount of FunctionalTabLayout to fetch fresh data
        setFunctionalRefreshKey((prev) => prev + 1);
      }

      // Load code ontologies when Code tab is clicked
      if (tabValue === "Code") {
        setShouldLoadCode(true);

        if (shouldLoadCode) {
          refetchCodeOntologies();
        }
      }
    },
    [shouldLoadArchitecture, refetchArchitecture, shouldLoadCode, refetchCodeOntologies, queryClient]
  );

  // A caller that opens the panel on a tab while it is already open (e.g. the ontology page's History button) sets a
  // new tabRequestedAt: switch to that tab even though the panel keeps its own tab otherwise.
  useEffect(() => {
    if (panelProps?.tabRequestedAt && panelProps.tab) handleTabChange(panelProps.tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelProps?.tabRequestedAt]);

  // Set initial tab state based on project ID and refresh data on mount
  useEffect(() => {
    if (!id) return;

    // Enable architecture tab if we have a project ID
    const architectureAvailable = !!id;
    setHasArchitecture(architectureAvailable);

    // Invalidate and reset functional graph queries when component mounts
    // This ensures fresh data when navigating to Semantic Model from sidebar
    queryClient.invalidateQueries({
      queryKey: ["functional-graph"],
      refetchType: "active",
    });
    queryClient.resetQueries({
      queryKey: ["functional-graph"],
      exact: false,
    });

    // Set initial active tab based on panelProps or default to Functional
    setActiveTab((prev) => {
      if (prev) return prev;
      // Check if panelProps has a tab specified
      if (panelProps?.tab) {
        return panelProps.tab;
      }
      return "Functional"; // Default to Functional tab
    });

    // When the panel is opened directly on the Architecture tab via
    // panelProps (e.g. from the dashboard Architecture tile), the user never
    // clicks the tab so handleTabChange never runs — flip the load flag here
    // so the graph query enables and category counts populate on first render.
    if (panelProps?.tab === "Architecture") {
      setShouldLoadArchitecture(true);
    }
  }, [id, queryClient, panelProps?.tab]);

  // NEW: Process architecture graph data when it loads
  useEffect(() => {
    if (architectureGraphData?.architectural_specs) {
      // Update architecture availability based on actual data
      const hasArchitectureData =
        Object.keys(architectureGraphData.architectural_specs).length > 0;

      if (hasArchitectureData) {
        const flattenedData = flattenArchitecturalSpecs(
          architectureGraphData.architectural_specs
        );
        setArchitectureData(
          flattenedData.length === 0
            ? architectureGraphData.architectural_specs
            : flattenedData
        );
      } else {
        // Even with no data, set the structure so categories are available
        setArchitectureData(architectureGraphData.architectural_specs || {});
      }

      // Update tabs to show Architecture as enabled since we now have the endpoint response
      setTabs((prevTabs) =>
        prevTabs.map((tab) => {
          if (tab.name === "Architecture") {
            return { ...tab, enabled: true };
          }
          return tab;
        })
      );
    }
  }, [architectureGraphData]);

  // Update tab enable/disable state reactively
  useEffect(() => {
    setTabs((prevTabs) =>
      prevTabs.map((tab) => {
        if (tab.name === "Functional") return { ...tab, enabled: true }; // Always enable Functional
        if (tab.name === "Design") return { ...tab, enabled: true }; // Always enable Design
        if (tab.name === "Architecture")
          return { ...tab, enabled: hasArchitecture };
        return tab;
      })
    );
  }, [hasArchitecture]);

  // Provide header actions to parent
  useEffect(() => {
    if (onHeaderActionsReady) {
      onHeaderActionsReady(
        <UnifiedModelHeaderActions
          isLoading={isLoadingArchitecture}
          onOpenNeo4jPopup={handleOpenNeo4jPopup}
        />
      );
    }
  }, [onHeaderActionsReady, isLoadingArchitecture, handleOpenNeo4jPopup]);

  // Listen for auto-open Neo4j event from upload completion
  useEffect(() => {
    const handleAutoOpenNeo4j = () => {
      handleOpenNeo4jPopup();
    };

    window.addEventListener("openNeo4jFromUpload", handleAutoOpenNeo4j);
    return () => {
      window.removeEventListener("openNeo4jFromUpload", handleAutoOpenNeo4j);
    };
  }, [handleOpenNeo4jPopup]);

  return (
    <div className="h-full">
      <Tabs
        value={activeTab}
        onValueChange={handleTabChange} // Use our custom handler
        className="flex-col flex h-full"
      >
        <div className="flex items-center border-b border-surface-border bg-surface">
          {tabs.map(
            (tab) =>
              tab.show && (
                <button
                  key={tab.name}
                  onClick={() => handleTabChange(tab.name)}
                  disabled={!tab.enabled}
                  className={clsx(
                    "relative flex items-center gap-1.5 px-4 py-2.5 text-lg font-medium transition-colors disabled:opacity-50",
                    activeTab === tab.name
                      ? "text-primary"
                      : "text-muted hover:text-text-primary"
                  )}
                >
                  <span>{tab.name}</span>
                  {tab.name === "Architecture" &&
                    activeTab === "Architecture" &&
                    isLoadingArchitecture && (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    )}
                  {activeTab === tab.name && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t" />
                  )}
                </button>
              )
          )}
        </div>
        <div className="flex-1 overflow-y-auto px-3 pb-4">
          <TabsContent value="Functional">
            {activeTab === "Functional" && (
              <FunctionalTabLayout
                key={functionalRefreshKey}
                onImportCsvClick={handleImportCsv}
              />
            )}
          </TabsContent>
          <TabsContent value="Design">
            {activeTab === "Design" && <DesignTabLayout />}
          </TabsContent>
          <TabsContent value="Architecture">
            {/* Show loading state while architecture data is loading */}
            {isLoadingArchitecture ? (
              <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-white" />
                <span className="ml-2 text-white">
                  Loading architecture specifications...
                </span>
              </div>
            ) : (
              <ArchitectureTabLayout
                architectureData={architectureData}
                setArchitectureData={setArchitectureData}
                setTabs={setTabs}
                projectUuid={id}
                graphData={architectureGraphData} // Pass graph data
                isLoadingGraph={isLoadingArchitecture} // Pass loading state
                onVisualizeArchitectureClick={handleVisualizeArchitecture}
                hasFunctionalData={hasFunctionalData}
              />
            )}
          </TabsContent>
          <TabsContent value="History" className="h-full mt-0">
            {activeTab === "History" && <NodeHistoryTabLayout />}
          </TabsContent>
          <TabsContent value="Code">
            {activeTab === "Code" && (
              <>
                {isLoadingCodeOntologies ? (
                  <div className="flex justify-center items-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-white" />
                    <span className="ml-2 text-white">Loading repositories...</span>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h2 className="text-xl font-semibold text-white mb-1">Code Repositories</h2>
                        <p className="text-sm text-muted">
                          Repositories indexed for ontology generation. Cluster documents are extracted from closely related files.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <CustomTooltip text={isViewer ? "View access only" : "Add Repository"} side="top">
                          <Button
                            onClick={() => {
                              setPanelType(null);
                              navigate(`/code-ontology/${id}`);
                            }}
                            disabled={isViewer}
                            variant="outline"
                            size="sm"
                            className="border-surface-border text-white hover:bg-surface-hover hover:border-primary gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Code className="h-4 w-4" />
                            Add Repository
                          </Button>
                        </CustomTooltip>
                        <Button
                          onClick={() => refetchCodeOntologies()}
                          variant="outline"
                          size="icon"
                          className="border-surface-border text-white hover:bg-surface-hover hover:border-primary"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {(!codeOntologiesData?.data || codeOntologiesData.data.length === 0) ? (
                      <div className="flex flex-col items-center justify-center h-48 gap-3 text-center">
                        <div className="w-12 h-12 rounded-full bg-surface-hover flex items-center justify-center">
                          <Code className="w-6 h-6 text-muted" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">No repositories found</p>
                          <p className="text-xs text-muted mt-1">Add a repository from the Ontology Generation page</p>
                        </div>
                        <Button
                          onClick={() => navigate(`/ontology/${id}/functional`)}
                          variant="outline"
                          size="sm"
                          className="mt-2 border-surface-border text-white hover:bg-surface-hover hover:border-primary"
                        >
                          Go to Ontology Generation
                        </Button>
                      </div>
                    ) : (
                      <div className="grid gap-3">
                        {codeOntologiesData.data.map((repo: CodeOntology) => {
                          const fileGraphStatus = repo.fileGraphStatus;
                          const isActive = fileGraphStatus === "active";
                          const isError = fileGraphStatus === "error";
                          const isIndexing = fileGraphStatus && !["active", "error"].includes(fileGraphStatus);

                          return (
                            <div
                              key={repo._id}
                              className="p-4 rounded-lg border border-surface-border bg-surface-card hover:border-surface-border/80 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-surface-hover flex items-center justify-center flex-shrink-0">
                                  <Code className="w-5 h-5 text-muted" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h3 className="text-sm font-semibold text-white truncate">{repo.name}</h3>
                                    {repo.language && (
                                      <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 whitespace-nowrap">
                                        {repo.language}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 text-xs">
                                    <span className={
                                      isIndexing ? "text-blue-400" :
                                      isActive ? "text-green-400" :
                                      isError ? "text-red-400" :
                                      "text-muted"
                                    }>
                                      {isIndexing
                                        ? `Indexing (${fileGraphStatus?.replace(/_/g, " ")})`
                                        : isActive ? "Indexed"
                                        : isError ? "Indexing Error"
                                        : "Pending"}
                                    </span>
                                    {repo.clusterCount != null && (
                                      <span className="text-muted">
                                        {repo.clusterCount} cluster{repo.clusterCount !== 1 ? "s" : ""}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <Button
                                  onClick={() => {
                                    setPanelType(null);
                                    navigate(`/code-ontology/${id}/nodes/${repo._id}`);
                                  }}
                                  variant="outline"
                                  size="sm"
                                  className="flex-shrink-0 border-surface-border text-white hover:bg-surface-hover hover:border-primary"
                                >
                                  View
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </div>
      </Tabs>

      {/* Architecture Generation Confirmation Dialog */}
      <ConfirmDialog
        open={isArchitectureConfirmOpen}
        onOpenChange={() => setArchitectureConfirmOpen(false)}
        onConfirm={handleConfirmGenerateArchitecture}
        title="Confirm Architecture Generation"
        description="Generating architecture will compute additional architecture metrics on top of existing ones. This is an expensive operation that may take several minutes. Additional metrics can be removed later if needed. Are you sure you want to proceed?"
      />
    </div>
  );
}
